/**
 * Build the URL for the Resource Landing Page (community-library /share route).
 *
 * In production this stays as a relative `/share/...` path so Vercel's edge rewrite
 * (configured in vercel.mjs) proxies the request to the diffit-community-library
 * deployment. The browser keeps the user on app.diffit.me for cookies/session.
 *
 * In local development the rewrite isn't applied (vite dev doesn't read vercel.mjs),
 * so we redirect directly to the community-library dev server — VITE_SHARE_BASE_URL
 * when set (e.g. an isolated worktree env on its own port), else localhost:3001.
 *
 * @param {string} packetId
 * @param {Object} [params] - extra query-string params (e.g. { exported: '1', format: 'slides' })
 * @returns {string}
 */
export function shareLandingUrl(packetId, params = {}) {
  const isLocalDev =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  const base = isLocalDev ? import.meta.env.VITE_SHARE_BASE_URL || 'http://localhost:3001' : '';
  const search = new URLSearchParams(params).toString();
  return `${base}/share/${packetId}${search ? `?${search}` : ''}`;
}

/**
 * Append the standard packet-share UTM params to a URL. Mirrors the styling used
 * for share page links in diffit-community-library (`withUtm` in src/lib/utils.js).
 *
 * @param {string} url
 * @param {string} source - utm_source value
 * @param {string} [medium] - utm_medium value (defaults to 'social')
 * @returns {string}
 */
export function withUtm(url, source, medium = 'social') {
  const u = new URL(url);
  u.searchParams.set('utm_source', source);
  u.searchParams.set('utm_medium', medium);
  u.searchParams.set('utm_campaign', 'packet_share');
  return u.toString();
}

/**
 * Append the sharer's referral code to a URL, so a link a teacher hands out credits them when
 * the recipient signs up. `?ref=` is what captureReferralFromUrl() reads on landing.
 *
 * `linkSource` (a value from src/constants/linkSources.js) rides along as `?ref_src=`, naming
 * the surface this link came off. Without it a copied packet link, a QR code and a forwarded
 * email are indistinguishable once attributed.
 *
 * Returns `url` untouched when there's no code — signed out, or a user whose code hasn't been
 * minted yet — so no caller has to branch on it.
 *
 * @param {string} url
 * @param {string} [referralCode]
 * @param {string} [linkSource]
 * @returns {string}
 */
export function withReferral(url, referralCode, linkSource) {
  if (!referralCode) return url;
  // Base supplied so a relative path resolves in the browser; every current caller passes an
  // absolute URL, and withUtm (which runs first in buildShareUrl) requires one anyway.
  const u = new URL(url, typeof window !== 'undefined' ? window.location.origin : undefined);
  u.searchParams.set('ref', referralCode);
  if (linkSource) u.searchParams.set('ref_src', linkSource);
  return u.toString();
}

/**
 * The canonical outbound share link: UTM params plus the sharer's referral code.
 *
 * Every copy-a-link surface should build its URL through here **and render the same value it
 * copies**. Several of them used to display the bare URL while the copy button wrote a
 * decorated one, so a user who selected the text out of the input got a different link than the
 * button gave them — and would have lost the referral credit with it.
 *
 * @param {string} url
 * @param {Object} [options]
 * @param {string} [options.source] - utm_source (defaults to the copy-link surface)
 * @param {string} [options.medium] - utm_medium
 * @param {string} [options.referralCode]
 * @param {string} [options.linkSource] - ref_src, from src/constants/linkSources.js
 * @returns {string}
 */
export function buildShareUrl(
  url,
  { source = 'copy_link', medium = 'referral', referralCode, linkSource } = {}
) {
  return withReferral(withUtm(url, source, medium), referralCode, linkSource);
}
